import React from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { LogOut, FileText, Activity, Shield, Coins, Globe, TrendingUp } from "lucide-react";
import WorkAuthDialog from "./WorkAuthDialog";

const HeaderNav = ({ filename, onHome }) => {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = React.useState(false);
  const navigate = useNavigate();

  const goHome = () => {
    if (onHome) onHome();
    else navigate(user ? "/app" : "/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <button onClick={goHome} title="Back to start" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="relative">
            <Activity className="w-6 h-6 text-sky-600" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          </div>
          <span className="flex flex-col leading-none text-left">
            <span className="font-extrabold tracking-tight text-slate-900 text-lg">tomorrows<span className="text-sky-600">job</span></span>
            <span className="text-[9px] font-mono uppercase tracking-widest text-slate-500">Career-os for you</span>
          </span>
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          {user && (
            <Button variant="ghost" onClick={() => setAuthOpen(true)} className="h-9 px-2.5 text-slate-700 hover:text-sky-700 hover:bg-slate-100" title="Where can you legally work?" data-testid="nav-work-auth-button">
              <Globe className="w-4 h-4 sm:mr-1.5" /> <span className="hidden md:inline text-sm">{user.work_authorizations?.length ? `Work rights (${user.work_authorizations.length})` : "Work rights"}</span>
            </Button>
          )}
          {user && (
            <Button variant="ghost" onClick={() => navigate("/accelerator")} className="h-9 px-2.5 text-slate-700 hover:text-sky-700 hover:bg-slate-100" data-testid="nav-accelerator-button">
              <Coins className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline text-sm">2nd Income</span>
            </Button>
          )}
          {user && (
            <Button variant="ghost" onClick={() => navigate("/future-jobs")} className="h-9 px-2.5 text-slate-700 hover:text-sky-700 hover:bg-slate-100" data-testid="nav-future-jobs-button">
              <TrendingUp className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline text-sm">Future Jobs</span>
            </Button>
          )}
          {filename && (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-300 text-xs text-slate-700 font-mono" data-testid="current-file-pill">
              <FileText className="w-3.5 h-3.5 text-sky-600" />
              {filename.length > 28 ? filename.slice(0, 28) + "…" : filename}
            </span>
          )}
          {user && (
            <div className="flex items-center gap-2">
              {user.is_admin && (
                <Button variant="outline" size="sm" onClick={() => { sessionStorage.removeItem("tj-skip-admin-redirect"); navigate("/admin"); }} className="border-sky-200 text-sky-700 hover:bg-sky-50 h-8" data-testid="nav-admin-button">
                  <Shield className="w-3.5 h-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Admin</span>
                </Button>
              )}
              {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-300" />}
              <span className="hidden sm:block text-sm text-slate-700">{user.name}</span>
              <Button variant="ghost" size="icon" onClick={logout} className="text-slate-500 hover:text-rose-600 hover:bg-slate-100" data-testid="logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <WorkAuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
};

export default HeaderNav;
